import * as React from "react";
import { css } from "@emotion/core";
import {
  Dialog,
  Button,
  Spinner,
  NonIdealState,
  Icon,
  InputGroup,
} from "@blueprintjs/core";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ApiAdmin, { IMaterial } from "../../../api/admin";
import { useSinkState } from "../../../context/sink";

interface FolderSettingsDialogProps {
  folder: { id: number; label: string };
  organizationId: number | undefined;
  onClose: () => void;
}

interface MaterialsTabProps {
  folder: { id: number; label: string };
  organizationId: number | undefined;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

const MaterialsTab: React.FC<MaterialsTabProps> = ({
  folder,
  organizationId,
  onSave,
  onCancel,
  isSaving,
}) => {
  const { sink } = useSinkState();
  const queryClient = useQueryClient();
  const [selectedMaterialIds, setSelectedMaterialIds] = React.useState<
    Set<number>
  >(new Set());
  const [filterQuery, setFilterQuery] = React.useState<string>("");

  const orgMaterials = React.useMemo(() => {
    if (!organizationId) return [];
    if (!sink.materials || !Array.isArray(sink.materials)) {
      console.warn("Materials not available in sink state", {
        hasMaterials: !!sink.materials,
        materialsType: typeof sink.materials,
        organizationId,
      });
      return [];
    }
    return sink.materials.filter(
      (m: IMaterial) =>
        (!m.organization_id || m.organization_id === organizationId) &&
        (!m.archived || m.archived === 0)
    );
  }, [sink.materials, organizationId]);

  const filteredMaterials = React.useMemo(() => {
    if (!filterQuery.trim()) return orgMaterials;
    const query = filterQuery.toLowerCase();
    return orgMaterials.filter((material: IMaterial) =>
      material.name.toLowerCase().includes(query)
    );
  }, [orgMaterials, filterQuery]);

  const {
    data: folderMaterialsData,
    isLoading: isLoadingFolderMaterials,
    error: folderMaterialsError,
  } = useQuery({
    queryKey: ["folderMaterials", folder.id],
    queryFn: () =>
      organizationId
        ? ApiAdmin.getFolderMaterials(organizationId, folder.id)
        : Promise.resolve({ material_ids: [] }),
    enabled: !!organizationId,
  });

  React.useEffect(() => {
    if (folderMaterialsData?.material_ids) {
      setSelectedMaterialIds(new Set(folderMaterialsData.material_ids));
    }
  }, [folderMaterialsData]);

  const updateMutation = useMutation({
    mutationFn: (materialIds: number[]) =>
      organizationId
        ? ApiAdmin.updateFolderMaterials(organizationId, folder.id, {
            material_ids: materialIds,
          })
        : Promise.reject(new Error("Organization ID is required")),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["folderMaterials", folder.id],
      });
      onSave();
    },
    onError: (error: Error) => {
      console.error("Failed to update folder materials:", error);
    },
  });

  const handleMaterialToggle = (materialId: number, checked: boolean) => {
    setSelectedMaterialIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(materialId);
      } else {
        next.delete(materialId);
      }
      return next;
    });
  };

  const handleSave = () => {
    const materialIds = Array.from(selectedMaterialIds);
    updateMutation.mutate(materialIds);
  };

  const isLoading = isLoadingFolderMaterials;
  const hasError = !!folderMaterialsError;
  const isPending = updateMutation.isPending;

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        `}
      >
        {isLoading ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 2rem;
            `}
          >
            <Spinner size={40} />
          </div>
        ) : hasError ? (
          <NonIdealState
            icon="error"
            title="Error Loading Materials"
            description={
              folderMaterialsError instanceof Error
                ? folderMaterialsError.message
                : "Failed to load folder materials"
            }
          />
        ) : orgMaterials.length === 0 ? (
          <NonIdealState
            icon="unresolve"
            title="No Materials Available"
            description={
              !sink.materials || !Array.isArray(sink.materials)
                ? "Materials are still loading. Please wait a moment and try again."
                : sink.materials.length === 0
                ? "No materials have been created for this organization yet."
                : "No active materials are available for this organization. All materials may be archived."
            }
          />
        ) : (
          <>
            <div
              css={css`
                padding: 0 1rem 1rem 1rem;
                border-bottom: 1px solid var(--neutral-5);
                flex-shrink: 0;
              `}
            >
              <InputGroup
                placeholder="Filter materials..."
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                leftIcon="search"
              />
            </div>
            <div
              css={css`
                overflow-y: auto;
                flex: 1;
                padding: 0.5rem;
              `}
            >
              {filteredMaterials.map((material: IMaterial) => (
                <div
                  key={material.id}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    &:hover {
                      background-color: var(--neutral-6);
                    }
                  `}
                  onClick={() =>
                    handleMaterialToggle(
                      material.id,
                      !selectedMaterialIds.has(material.id)
                    )
                  }
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 18px;
                      height: 18px;
                      background-color: var(--neutral-6);
                      border: 1px solid var(--neutral-5);
                      border-radius: 3px;
                    `}
                  >
                    <Icon
                      icon="tick"
                      color={
                        selectedMaterialIds.has(material.id)
                          ? "var(--neutral-3)"
                          : "var(--neutral-6)"
                      }
                      size={14}
                    />
                  </div>

                  <span>{material.name}</span>
                </div>
              ))}
            </div>
            {updateMutation.isError && (
              <div
                css={css`
                  color: var(--danger);
                  font-size: 0.875rem;
                  padding: 0.5rem;
                `}
              >
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : "Failed to save materials"}
              </div>
            )}
          </>
        )}
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-top: 1px solid var(--neutral-5);
          flex-shrink: 0;
        `}
      >
        <Button text="Cancel" disabled={isPending} onClick={onCancel} />
        <Button
          text={isPending ? "Saving..." : "Save"}
          intent="primary"
          disabled={isLoading || hasError || isPending}
          onClick={handleSave}
        />
      </div>
    </>
  );
};

export const FolderSettingsDialog: React.FC<FolderSettingsDialogProps> = ({
  folder,
  organizationId,
  onClose,
}) => {
  const handleSave = () => {
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      className="bp6-dark"
      isOpen={true}
      onClose={onClose}
      title="Manage Folder Materials"
      css={css`
        width: 500px;
        max-width: 90vw;
      `}
    >
      <div
        css={css`
          height: 50vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 2rem 2rem 0 1rem;
            flex-shrink: 0;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <Icon icon="folder-open" size={32} color="var(--neutral-3)" />
            <h2
              css={css`
                color: var(--neutral-3);
                margin: 1rem 0;
              `}
            >
              {folder.label}
            </h2>
          </div>
        </div>
        <div
          css={css`
            flex: 1;
            min-height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          `}
        >
          <MaterialsTab
            folder={folder}
            organizationId={organizationId}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={false}
          />
        </div>
      </div>
    </Dialog>
  );
};
